import { useMemo } from 'react';
import { DomainsDailyStatsResponse } from '../../../shared/models/monitor/data/sources/DomainsDailyStatsResponse';
import { DomainsStatsWithShare } from './UseDomainsStatsShare';
import { useDomainsStatsSorted } from './UseDomainsStatsSorted';

export const useDomainsDailyStatsMatrix = (
  sourcesDailyStatsForDomains: DomainsDailyStatsResponse,
  domainsStatsWithShare: DomainsStatsWithShare | undefined,
  projectDomain?: string | null,
  selectedDomains?: string[] | undefined | null
) => {
  // Use sorted hook to ensure consistent sorting with table
  const sortedDomains = useDomainsStatsSorted(domainsStatsWithShare);

  return useMemo(() => {
    if (!sourcesDailyStatsForDomains || Object.keys(sourcesDailyStatsForDomains).length === 0) {
      return null;
    }

    let domainNames: string[];

    // If selectedDomains are provided, use them (sorted by count)
    if (selectedDomains && selectedDomains.length > 0 && sortedDomains) {
      // Sort selected domains by count (descending) to match table sorting
      const domainCountMap = new Map(sortedDomains.map((d) => [d.domain, d.count]));
      domainNames = [...selectedDomains].sort((a, b) => {
        const countA = domainCountMap.get(a) || 0;
        const countB = domainCountMap.get(b) || 0;
        return countB - countA;
      });
    } else if (sortedDomains && sortedDomains.length > 0) {
      // Extract top 10 domain names from sorted domains (already sorted by count descending)
      domainNames = sortedDomains.slice(0, 10).map((d) => d.domain);

      // If user's domain exists in daily stats data and is not in top list, append it
      if (projectDomain && !domainNames.includes(projectDomain)) {
        const hasProjectDomainInData = Object.values(sourcesDailyStatsForDomains).some((dayStats) =>
          dayStats.some((stat) => stat.domain === projectDomain)
        );
        if (hasProjectDomainInData) {
          domainNames.push(projectDomain);
        }
      }
    } else {
      return null;
    }

    // Sort dates chronologically
    const sortedDates = Object.keys(sourcesDailyStatsForDomains).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    // Create matrix header
    const matrix: (string | number)[][] = [['date', ...domainNames]];

    // Process each date
    sortedDates.forEach((date) => {
      const dayStats = sourcesDailyStatsForDomains[date];

      // Create a map for quick lookup of domain counts
      const domainCountMap = new Map(dayStats.map((stat) => [stat.domain, stat.count]));

      // Create row for this date
      const row: (string | number)[] = [date];

      // Add count for each top domain (0 if not found)
      domainNames.forEach((domain) => {
        const count = domainCountMap.get(domain) || 0;
        row.push(count);
      });

      matrix.push(row);
    });

    return matrix;
  }, [sourcesDailyStatsForDomains, sortedDomains, projectDomain, selectedDomains]);
};
